import { textGray } from '@osmosis/tailwind-ui/styles/text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { H4 } from '@osmosis/tailwind-ui/components/Text';
import { useColors } from '@osmosis/tailwind-ui/helpers/useColors';
import { IconButtonProps } from '@osmosis/tailwind-ui/components/IconButton';
import { ButtonColors } from '@osmosis/tailwind-ui/styles/button';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { PropsWithChildren, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { faEye } from '@fortawesome/pro-solid-svg-icons/faEye';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { useUserContext } from '../../../../context/UserContext';
import ProgressBar from '../../../ProgressBar';
import Link from 'next/link';

import LanguageTag from '../../../LanguageTag';

export type AssessmentRowItemProps = {
  name: string;
  total: number;
  complete: number;
  customHeaderActions?: ReactNode;
  onStart?: (isResumeClicked: boolean) => void;
  onAdd?: () => void;
  onPreview?: () => void;
  languageTag?: boolean;
  quizUrl?: string;
};

const OptionIconButton = (props: IconButtonProps) => (
  <IconButton
    variant="outline"
    icon={<Icon icon={faEye} />}
    data-testid={props['data-testid']}
    {...props}
    className={`mr-1 text-[18px] ${textGray}`}
  />
);

const TooltipForIcon = ({
  children,
  id,
}: PropsWithChildren & { id: string }) => {
  const colors = useColors();

  return (
    <Tooltip
      id={id}
      place="bottom"
      style={{ backgroundColor: colors.text, color: colors.textInvert }}
    >
      <P color="inherit" className={`text-[13px]`}>
        {children}
      </P>
    </Tooltip>
  );
};

/**
 * AssessmentRowItem.
 */
const AssessmentRowItem = (props: AssessmentRowItemProps): JSX.Element => {
  const {
    name,
    total,
    complete,
    customHeaderActions,
    onPreview,
    onAdd,
    onStart,
    languageTag,
    quizUrl,
  } = props;

  const intl = useIntl();

  const message = {
    text: intl.formatMessage({
      id: 'learn.assessment.start',
      defaultMessage: 'Start',
    }),
    bg: 'blue' as ButtonColors,
  };
  const { hasPrime } = useUserContext();

  if (complete > 0 && complete < total && hasPrime) {
    message.text = intl.formatMessage({
      id: 'learn.assessment.resume',
      defaultMessage: 'Resume',
    });
    message.bg = 'olive';
  } else if (total === complete) {
    ((message.text = intl.formatMessage({
      id: 'learn.assessment.retake',
      defaultMessage: 'Retake',
    })),
      (message.bg = 'olive'));
  }
  const buttonStartAriaLabel = 'Start';

  return (
    <Box>
      <Box className={`mb-2`}>
        <Flex className={`justify-between items-center`}>
          <Flex className={`flex-1 flex-col`}>
            <H4 className={`mb-1`}>{name}</H4>
            {languageTag && (
              <Flex className={`mb-1`}>
                <LanguageTag size={10} />
              </Flex>
            )}
          </Flex>

          {customHeaderActions || (
            <Flex className={`items-center`}>
              {onPreview && (
                <>
                  <OptionIconButton
                    onClick={onPreview}
                    icon={<Icon icon={faEye} />}
                    data-testid={`assessments-${name}-preview-button`}
                    data-tip
                    data-tooltip-id="eye-icon"
                    // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                    aria-label="Quick view"
                  />

                  <TooltipForIcon id="eye-icon">
                    <FormattedMessage
                      id="lern.blocks.assessments_row.eye_icon"
                      defaultMessage="Quick view"
                    />
                  </TooltipForIcon>
                </>
              )}

              {onAdd && hasPrime && (
                <>
                  <OptionIconButton
                    onClick={onAdd}
                    icon={<Icon icon={faPlus} />}
                    data-testid={`assessments-${name}-add-button`}
                    data-tip
                    data-tooltip-id="plus-icon"
                    // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                    aria-label="Add to queue"
                  />

                  <TooltipForIcon id="plus-icon">
                    <FormattedMessage
                      id="lern.blocks.assessments_row.plus_icon"
                      defaultMessage="Add to queue"
                    />
                  </TooltipForIcon>
                </>
              )}

              {quizUrl ? (
                <Link
                  color={message.bg}
                  data-testid={`assessments-${name}-start-button`}
                  aria-label={buttonStartAriaLabel}
                  href={quizUrl}
                  className="text-white bg-blue-600 dark:bg-blue-300 disabled:bg-blue-600 dark:disabled:bg-blue-400 hover:bg-blue-700 dark:hover:bg-blue-400 active:bg-blue-800 dark:active:bg-blue-500 dark:text-slate-800 flex font-bold items-center py-[.5em] px-[1em] rounded justify-center text-sm"
                >
                  {message.text}
                </Link>
              ) : (
                <Button
                  onClick={() => onStart?.(message.text === 'Resume')}
                  size="s"
                  color={message.bg}
                  data-testid={`assessments-${name}-start-button`}
                  aria-label={buttonStartAriaLabel}
                >
                  {message.text}
                </Button>
              )}
            </Flex>
          )}
        </Flex>
        <P className={`opacity-[0.75]`}>
          <FormattedMessage
            defaultMessage="{complete} / {total} complete"
            id="learn.assessments.progress"
            values={{
              complete: hasPrime ? complete : 0,
              total,
            }}
          />
        </P>
      </Box>
      <ProgressBar
        progress={(hasPrime ? complete : 0) / total}
        barColor="olive"
        height={7}
      />
    </Box>
  );
};

export default AssessmentRowItem;
